import React, {useMemo, useState, useEffect} from 'react';
import { datadogRum  } from '@datadog/browser-rum';

import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';

import ontLogo from '../ont_logo.svg';
import illuminaLogo from '../illumina_logo.svg';
import CreateONTRunForm from './CreateONTRunForm';
import CreateIlluminaRunForm from './CreateIlluminaRunForm';
import { sequencerSerialNumberMetadata, stripSequencerSerialNumberPrefix, stripFlowCellSerialNumberPrefix, getFlowCellSerialNumberPrefix } from './canonicalizeMetadata';
import { slugify } from '../utils';
import { useCreateDataset, useUpdateDataset } from '../services/api/KeynomeAPIMutations';

function getFriendlyDatasetName(userName) {
    const randomChars = Math.random().toString(36).slice(8).toUpperCase()
    const date = new Date().toISOString().split('T')[0]
    let friendlyName = `${date} Dataset ${randomChars}`

    if (userName) {
        const firstName = userName.split(' ')[0]
        friendlyName = `${firstName}'s ${friendlyName}`
    }
    
    return friendlyName
}

function validateSequencerSerialNumber(sequencer, sequencerSerialNumber) {
    // Be permissive of sequencer serial number for unknown sequencers
    if (sequencer === '') {
        return true
    }

    // Consider an unset serial number valid
    if (sequencerSerialNumber === '') {
        return true
    }

    return (new RegExp("_?[A-Z0-9]{5,6}(-[A-Z0-9]{1,2})?$", "i").test(stripSequencerSerialNumberPrefix(sequencerSerialNumber)))
}

function validateFlowCellSerialNumber(flowCell, flowCellSerialNumber) {
    // Be permissive of flow cell serial number for unknown flow cells
    if (flowCell === '') {
        return true
    }

    // Consider an unset serial number valid
    if (flowCellSerialNumber === '') {
        return true 
    }

    return new RegExp("^[A-Z][0-9]{5}$", "i").test(stripFlowCellSerialNumberPrefix(flowCellSerialNumber))
}

function AddRunDialog(props) {
    const { open, setOpen, activeProject, userData, reloadRuns, setUserErrorMessage, activeDataset } = props;

    const defaultFriendlyName = activeDataset?.attributes?.name || getFriendlyDatasetName();

    const [isCreating, setIsCreating] = useState(false);
    const [platform, setPlatform] = useState('ont');
    const [friendlyName, setFriendlyName] = useState(defaultFriendlyName);
    const [name, setName] = useState(slugify(defaultFriendlyName));
    const [sequencer, setSequencer] = useState('');
    const [sequencerSerialNumber, setSequencerSerialNumber] = useState('');
    const [flowCell, setFlowCell] = useState('');
    const [flowCellSerialNumber, setFlowCellSerialNumber] = useState('');
    const [barcodingKit, setBarcodingKit] = useState('');
    const [libraryPrepKit, setLibraryPrepKit] = useState('');
    const [sequencerSerialNumberIsValid, setSequencerSerialNumberIsValid] = useState(true);
    const [flowCellSerialNumberIsValid, setFlowCellSerialNumberIsValid] = useState(true);
    const [description, setDescription] = useState('');
    const [flowCellPrefix, setFlowCellPrefix] = useState('');

    const { mutateAsync: createDataset } = useCreateDataset();
    const { mutateAsync: updateDataset } = useUpdateDataset();

    useEffect(() => {
        const validSequencerSerialNumber = validateSequencerSerialNumber(sequencer, sequencerSerialNumber) 
        const validFlowCellSerialNumber = validateFlowCellSerialNumber(flowCell, flowCellSerialNumber)
        setSequencerSerialNumberIsValid(validSequencerSerialNumber)
        setFlowCellSerialNumberIsValid(validFlowCellSerialNumber)
    }, [flowCell, sequencer, flowCellSerialNumber, sequencerSerialNumber])

    useEffect(() => {
        setFriendlyName(activeDataset?.attributes?.name || getFriendlyDatasetName(userData?.data.attributes.name))
    }, [userData, activeDataset]);

    useEffect(() => {
        setName(slugify(friendlyName))
    }, [friendlyName]);

    useEffect(() => {
        if (!open) {
            return
        }
        if (activeDataset) {
            setPlatform(activeDataset.attributes.sequencing_platform || 'ont');
            setFriendlyName(activeDataset.attributes.name || '')
            setName(slugify(activeDataset.attributes.name || ''))
            setSequencer(activeDataset.attributes.sequencer_name || '');
            setSequencerSerialNumber(activeDataset.attributes.minion_sn || '');
            setFlowCell(activeDataset.attributes.flowcell_name || '');
            setFlowCellSerialNumber(activeDataset.attributes.flowcell_sn || '');
            setBarcodingKit(activeDataset.attributes.barcoding_kit || '');
            setLibraryPrepKit(activeDataset.attributes.library_prep_kit || '');
            setDescription(activeDataset.attributes.description || '');
            const flowCellSerialNumberPrefix = getFlowCellSerialNumberPrefix(activeDataset.attributes.flowcell_name, activeDataset.attributes.flowcell_sn)
            setFlowCellPrefix(flowCellSerialNumberPrefix || 'PA');
        }
        else {
            const datasetName = getFriendlyDatasetName(userData?.data.attributes.name)
            setPlatform('ont');
            setFriendlyName(datasetName)
            setName(slugify(datasetName))
            setSequencer('');
            setSequencerSerialNumber('');
            setFlowCell('');
            setFlowCellSerialNumber('');
            setBarcodingKit('');
            setLibraryPrepKit('');
            setDescription('');
        }
    }, [open, userData, activeDataset])

    const handleClose = (event, reason) => {
        if (reason && reason === "backdropClick") {
            return;
        }

        setOpen(false);
    };

    const handlePlatformUpdate = (event, newPlatform) => {
        if (newPlatform === null) {
            return;
        }

        setPlatform(newPlatform)
        setSequencer('');
        setSequencerSerialNumber('');
        setFlowCell('');
        setFlowCellSerialNumber('');
        setBarcodingKit('');
        setLibraryPrepKit('');
        setDescription('');
    };

    const upsertRun = async () => {
        setIsCreating(true)
        const attributes = {
            name: name,
            sequencing_platform: platform,
            sequencer_name: sequencer,
            minion_sn: sequencer !== '' && sequencerSerialNumber !== '' ? `${sequencerSerialNumberMetadata[sequencer].prefix}${stripSequencerSerialNumberPrefix(sequencerSerialNumber)}` : null,
            flowcell_name: flowCell !== '' ? flowCell : null,
            flowcell_sn: flowCell !== '' && flowCellSerialNumber !== '' ? `${flowCellPrefix}${stripFlowCellSerialNumberPrefix(flowCellSerialNumber)}` : null,
            barcoding_kit: barcodingKit !== '' ? barcodingKit : null,
            library_prep_kit: libraryPrepKit !== '' ? libraryPrepKit : null,
            description: description !== '' ? description : null,
        }

        try {
            if (activeDataset) {
                await updateDataset({
                    datasetId: activeDataset.id,
                    attributes
                });
            } else {
                await createDataset({
                    projectId: activeProject.id,
                    attributes
                });
            }
            reloadRuns();
            setOpen(false);
        } catch (error) {
            const action = activeDataset ? 'update' : 'create';
            if (error.response?.status === 422) {
                setUserErrorMessage(`Failed to ${action} dataset record in Keynome Cloud! Please confirm that the metadata is correct and try again. Contact support@dayzerodiagnostics.com if you require assistance.`);
            } else {
                setUserErrorMessage(`Failed to ${action} dataset record in Keynome Cloud! Please wait a few minutes and try again. Contact support@dayzerodiagnostics.com if you require assistance.`);
            }
            datadogRum.addError(error);
        } finally {
            setIsCreating(false);
        }
    };

    let formContent = platform === 'ont' ? (
        <CreateONTRunForm 
            sequencer={sequencer}
            setSequencer={setSequencer}
            sequencerSerialNumber={sequencerSerialNumber}
            setSequencerSerialNumber={setSequencerSerialNumber}
            sequencerSerialNumberIsValid={sequencerSerialNumberIsValid}
            flowCell={flowCell}
            setFlowCell={setFlowCell}
            flowCellSerialNumber={flowCellSerialNumber}
            setFlowCellSerialNumber={setFlowCellSerialNumber}
            flowCellSerialNumberIsValid={flowCellSerialNumberIsValid}
            barcodingKit={barcodingKit}
            setBarcodingKit={setBarcodingKit}
            libraryPrepKit={libraryPrepKit}
            setLibraryPrepKit={setLibraryPrepKit}
            platform='ont'
            flowCellPrefix={flowCellPrefix}
            setFlowCellPrefix={setFlowCellPrefix}
        />
    ) : (
        <CreateIlluminaRunForm 
            sequencer={sequencer}
            setSequencer={setSequencer}
            flowCell={flowCell}
            setFlowCell={setFlowCell}
            barcodingKit={barcodingKit}
            setBarcodingKit={setBarcodingKit}
            libraryPrepKit={libraryPrepKit}
            setLibraryPrepKit={setLibraryPrepKit}
            platform='illumina'
        />
    )

    const formIsValid = useMemo(() => {
        return sequencerSerialNumberIsValid && flowCellSerialNumberIsValid
    }, [sequencerSerialNumberIsValid, flowCellSerialNumberIsValid]);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="sm"
        >
            <DialogTitle>{activeDataset ? 'Update' : 'Add New'} Sequencing Dataset</DialogTitle>
            <DialogContent>
                <Box sx={{ paddingTop: "10px" }}>
                    <Grid container>
                        <Grid item xs={12}>
                            <Box sx={{ padding: "0px 10px" }}>
                                <ToggleButtonGroup
                                    fullWidth
                                    color="primary"
                                    value={platform}
                                    exclusive
                                    onChange={handlePlatformUpdate}
                                    aria-label="Platform"
                                    disabled={!!activeDataset}
                                >
                                    <ToggleButton value="ont" data-cy="create-ont-dataset" sx={{ fontSize: "15px", fontWeight: "bold" }}>
                                        <img src={ontLogo} alt="platform-logo" height="20px" style={{ marginLeft: '4px', marginRight: '10px' }} />
                                        ONT
                                    </ToggleButton>
                                    <ToggleButton value="illumina" data-cy="create-illumina-dataset" sx={{ fontSize: "15px", fontWeight: "bold" }}>
                                        <img src={illuminaLogo} alt="platform-logo" height="20px" style={{ marginLeft: '4px', marginRight: '10px' }} />
                                        Illumina
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sx={{ padding: '15px 5px' }}>
                            <Box sx={{ padding: "5px" }}>
                                <TextField
                                    error={name === ''}
                                    required
                                    fullWidth
                                    variant="filled"
                                    data-cy="dataset-name-input"
                                    label="Name"
                                    value={friendlyName}
                                    helperText={`Required. ${friendlyName === '' ? '' : `Identifier: ${name}`}`}
                                    onChange={(event) => {
                                        setFriendlyName(event.target.value);
                                    }}
                                />
                            </Box>
                        </Grid>
                        {formContent}
                        <Grid item xs={12}>
                            <Box sx={{ padding: "15px 10px" }}>
                                <TextField
                                    id="description-textarea"
                                    label="Description"
                                    placeholder="Short description of sequencing dataset"
                                    value={description}
                                    helperText="Optional"
                                    variant="standard"
                                    multiline
                                    fullWidth
                                    maxRows={2}
                                    onChange={(event) => {
                                        setDescription(event.target.value.slice(0, 100));
                                    }}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            </DialogContent>
            <DialogActions sx={{ marginBottom: "15px" }}>
                <Button variant="contained" color="primary" data-cy="create-dataset" sx={{ marginRight: "20px" }} onClick={upsertRun} disabled={(name === '' || isCreating || !formIsValid || !activeProject)}>{activeDataset ? 'Update' : 'Create'}</Button>
                <Button color="primary" sx={{ marginRight: "20px" }} onClick={handleClose}>Cancel</Button>
            </DialogActions>
        </Dialog>
    );

}


export default AddRunDialog;
